'use client';

import React from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { settingAPI } from '@/lib/api';

/**
 * 全局页面Banner组件
 * 在所有非首页页面显示，位于页面顶部（Header之后，面包屑之前）
 * 参考：http://demo7.wp.taodakeji.com/contact-us
 * 
 * Banner图片从CMS设置中读取（page_banner_image）
 */
export default function PageBanner() {
  // 默认使用默认图片，确保始终有图片显示
  const [bannerImage, setBannerImage] = React.useState<string>('/wp-content/uploads/2022/05/s1-1024x160.jpg');
  const [isLoading, setIsLoading] = React.useState(false); // 初始为false，立即显示默认图片
  const [isClient, setIsClient] = React.useState(false);
  
  // Hook 必须在顶层调用 - 所有 Hooks 必须在条件返回之前调用
  const pathname = usePathname();
  
  // 确保只在客户端执行
  React.useEffect(() => {
    setIsClient(true);
  }, []);

  // 从设置API获取Banner图片 - 必须在条件返回之前调用
  React.useEffect(() => {
    let isMounted = true; // 防止组件卸载后更新状态
    
    const fetchBannerImage = async () => {
      try {
        setIsLoading(true);
        const settings = await settingAPI.getAll();
        
        if (!isMounted) return; // 如果组件已卸载，不更新状态
        
        console.log('[PageBanner] Settings from API:', settings);
        console.log('[PageBanner] page_banner_image value:', settings?.page_banner_image);
        
        const imageUrl = settings?.page_banner_image;
        
        if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '') {
          // 构造完整URL（与其他组件保持一致的方式）
          let finalImageUrl: string;
          
          if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            // 如果已经是完整URL，直接使用
            finalImageUrl = imageUrl;
          } else {
            // 如果是相对路径，构造完整URL
            const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api';
            const baseUrl = apiBaseUrl.replace('/api', '');
            const path = imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl;
            finalImageUrl = `${baseUrl}${path}`;
          }
          
          console.log('[PageBanner] Original imageUrl:', imageUrl);
          console.log('[PageBanner] Final imageUrl:', finalImageUrl);
          
          if (isMounted) {
            setBannerImage(finalImageUrl);
            setIsLoading(false);
          }
        } else {
          // 如果没有设置，使用默认图片
          console.log('[PageBanner] No banner image in settings, using default');
          if (isMounted) {
            setBannerImage('/wp-content/uploads/2022/05/s1-1024x160.jpg');
            setIsLoading(false);
          }
        }
      } catch (error: any) {
        console.error('[PageBanner] Failed to fetch page banner image:', error);
        // 出错时使用默认图片
        if (isMounted) {
          setBannerImage('/wp-content/uploads/2022/05/s1-1024x160.jpg');
          setIsLoading(false);
        }
      }
    };

    fetchBannerImage();
    
    // 清理函数
    return () => {
      isMounted = false;
    };
  }, []);

  // 现在所有 Hooks 都已调用，可以进行条件返回
  // 服务端渲染时不显示，避免 usePathname 问题
  if (!isClient) {
    return null;
  }

  const isHomePage = pathname === '/';
  console.log('[PageBanner] Component rendered, pathname:', pathname, 'isHomePage:', isHomePage, 'isClient:', isClient);

  // 首页不显示banner
  if (isHomePage) {
    console.log('[PageBanner] Homepage detected, not rendering banner');
    return null;
  }

  // 使用bannerImage，如果没有则使用默认图片
  const displayImage = bannerImage || '/wp-content/uploads/2022/05/s1-1024x160.jpg';
  const defaultImage = '/wp-content/uploads/2022/05/s1-1024x160.jpg';
  
  // 判断是否是外部URL（CMS后端的图片）还是本地public目录的图片
  const isExternalUrl = displayImage.startsWith('http://') || displayImage.startsWith('https://');

  console.log('[PageBanner] ===== Rendering PageBanner =====');
  console.log('[PageBanner] displayImage:', displayImage);
  console.log('[PageBanner] isExternalUrl:', isExternalUrl);
  console.log('[PageBanner] isLoading:', isLoading);

  console.log('[PageBanner] About to render banner section');

  return (
    <section 
      className="elementor-section elementor-top-section elementor-element elementor-element-795ff64 elementor-section-full_width elementor-section-height-default elementor-section-height-default"
      data-id="795ff64"
      data-element_type="section"
      style={{
        display: 'block',
        visibility: 'visible',
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        maxWidth: 'none',
        padding: 0,
        marginTop: 0,
        marginBottom: 0,
        overflow: 'hidden',
      }}
    >
      <div 
        className="elementor-container elementor-column-gap-no" 
        style={{ 
          maxWidth: '100%', 
          width: '100%',
          margin: 0,
          padding: 0,
        }}
      >
        <div 
          className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-1e63554"
          style={{
            width: '100%',
            padding: 0,
          }}
        >
          <div 
            className="elementor-widget-wrap elementor-element-populated"
            style={{
              width: '100%',
              padding: 0,
            }}
          >
            <div 
              className="elementor-element elementor-element-256082c elementor-widget elementor-widget-image"
              style={{
                width: '100%',
              }}
            >
              <div 
                className="elementor-widget-container" 
                style={{ 
                  width: '100%',
                  padding: 0,
                  margin: 0,
                }}
              >
                {/* 外部URL（CMS后端）使用普通img标签，本地public目录使用Next.js Image组件 */}
                {isExternalUrl ? (
                  <img
                    key={displayImage}
                    src={displayImage}
                    alt="Page Banner"
                    className="attachment-large size-large wp-image-382"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      maxWidth: '100%',
                      objectFit: 'cover',
                    }}
                    onLoad={() => {
                      console.log('[PageBanner] ✅ Image loaded successfully:', displayImage);
                    }}
                    onError={(e) => {
                      console.error('[PageBanner] ❌ Banner image load error:', displayImage);
                      const target = e.target as HTMLImageElement;
                      if (target && target.src !== defaultImage) {
                        console.log('[PageBanner] 🔄 Falling back to default image');
                        target.src = defaultImage;
                      }
                    }}
                  />
                ) : (
                  <Image
                    key={displayImage}
                    src={displayImage}
                    alt="Page Banner"
                    width={1920}
                    height={300}
                    className="attachment-large size-large wp-image-382"
                    priority
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      maxWidth: '100%',
                      objectFit: 'cover',
                    }}
                    sizes="100vw"
                    onLoad={() => {
                      console.log('[PageBanner] ✅ Image loaded successfully:', displayImage);
                    }}
                    onError={(e) => {
                      console.error('[PageBanner] ❌ Banner image load error:', displayImage);
                      const target = e.target as HTMLImageElement;
                      if (target && target.src !== defaultImage) {
                        console.log('[PageBanner] 🔄 Falling back to default image');
                        target.src = defaultImage;
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

